import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, RB100_LOGO, GOV_SPONSOR, GOLD_SPONSORS } from "../lib/api";
import { Loader2, Printer, AlertTriangle, Shield, Car, Camera, Heart, Share2, Instagram } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import ShareCard from "../components/ShareCard";

const TIER_DISPLAY = {
  car_owner: { label: "مالك سيارة", icon: Car, en: "CAR OWNER" },
  photographer: { label: "مصوّر النادي", icon: Camera, en: "PHOTOGRAPHER" },
  fan: { label: "هاوي ومتابع", icon: Heart, en: "CLUB FAN" },
};

export default function Badge() {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showShare, setShowShare] = useState(false);

  useEffect(() => {
    api.get(`/badge/${code}`)
      .then(({ data }) => setData(data))
      .catch((e) => setError(e?.response?.data?.detail || "تعذر تحميل الباج"))
      .finally(() => setLoading(false));
  }, [code]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="w-7 h-7 text-rb-red animate-spin" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white p-6">
        <div className="max-w-md text-center">
          <AlertTriangle className="w-12 h-12 text-rb-red mx-auto mb-4" />
          <h1 className="font-heading text-2xl font-bold mb-3 text-rb-ink">الباج غير متوفر</h1>
          <p className="text-rb-textMuted text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="badge-page" className="min-h-screen bg-rb-surface py-10 print:py-0 print:bg-white">
      <div className="no-print max-w-3xl mx-auto px-6 mb-8 text-center">
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-rb-ink mb-3">
          بطاقة عضوية النادي
        </h1>
        <p className="text-rb-textMuted text-sm mb-5">
          اطبع البطاقة، احفظها كصورة، أو شاركها على السوشال ميديا 🏁
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <button
            onClick={() => setShowShare(true)}
            data-testid="badge-share-btn"
            className="inline-flex items-center gap-2 bg-rb-red hover:bg-rb-redHover text-white font-bold px-7 py-3 transition-colors"
          >
            <Share2 className="w-5 h-5" />
            شارك بطاقتي
          </button>
          <button
            onClick={() => window.print()}
            data-testid="badge-print-btn"
            className="inline-flex items-center gap-2 border-2 border-rb-ink hover:bg-rb-ink hover:text-white text-rb-ink font-bold px-7 py-3 transition-colors bg-white"
          >
            <Printer className="w-5 h-5" />
            طباعة / حفظ كـ PDF
          </button>
        </div>
      </div>

      <div className="flex justify-center px-6 print:px-0">
        <BadgeCard data={data} />
      </div>

      {showShare && <ShareCard data={data} onClose={() => setShowShare(false)} />}
    </div>
  );
}

const BadgeCard = ({ data }) => {
  const mtype = data.membership_type || "car_owner";
  const tier = TIER_DISPLAY[mtype] || TIER_DISPLAY.car_owner;
  const TierIcon = tier.icon;
  const hasCarInfo = mtype === "car_owner" && (data.car_make || data.car_model);

  return (
    <div
      data-testid="badge-card"
      className="badge-print bg-white text-black w-full max-w-[480px] shadow-2xl flex flex-col overflow-hidden print:shadow-none print:max-w-none print:w-[105mm]"
      dir="rtl"
      style={{ minHeight: "720px" }}
    >
      {/* Top red stripe */}
      <div className="h-3 bg-rb-red shrink-0" />

      {/* Header */}
      <div className="bg-black text-white px-5 py-4 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <div className="bg-white p-1 h-16 w-16 flex items-center justify-center border border-white/40 shrink-0">
            <img src={RB100_LOGO} alt="" className="h-full w-full object-contain" />
          </div>
          <div>
            <div className="font-heading font-extrabold text-2xl leading-none">RB100</div>
            <div className="text-[9px] tracking-[0.3em] text-white/70 mt-1">SINCE 2002</div>
          </div>
        </div>
        <div className="text-left">
          <div className="text-[9px] tracking-[0.25em] text-white/70 uppercase">{tier.en}</div>
          <div className="font-heading font-bold text-sm flex items-center gap-1.5 justify-end">
            <TierIcon className="w-4 h-4 text-rb-red" />
            {tier.label}
          </div>
          <div className="text-[9px] text-white/60 mt-0.5">بطاقة عضوية</div>
        </div>
      </div>

      {/* Main content */}
      <div className="px-5 py-4 flex-1 flex flex-col gap-3">
        <div>
          <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase mb-1">اسم العضو</div>
          <div className="font-heading text-2xl font-extrabold leading-tight">
            {data.name}
          </div>
        </div>

        {/* Car owner: plate + car details */}
        {mtype === "car_owner" && data.plate_number && (
          <div className="border-t border-zinc-200 pt-2.5">
            <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase mb-1">لوحة السيارة</div>
            <div className="font-mono text-xl font-bold tracking-widest">{data.plate_number}</div>
          </div>
        )}

        {hasCarInfo && (
          <div className="border-t border-zinc-200 pt-2.5">
            <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase mb-1">السيارة</div>
            <div className="font-bold text-sm">
              {[data.car_year, data.car_make, data.car_model].filter(Boolean).join(" · ")}
            </div>
          </div>
        )}

        {/* Photographer: social handles */}
        {mtype === "photographer" && (data.instagram_handle || data.snapchat_handle) && (
          <div className="border-t border-zinc-200 pt-2.5 space-y-2">
            <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase mb-1">حسابات التصوير</div>
            {data.instagram_handle && (
              <div className="flex items-center gap-2 text-sm" dir="ltr">
                <Instagram className="w-4 h-4 text-rb-red shrink-0" />
                <span className="font-bold">@{data.instagram_handle}</span>
                <span className="text-zinc-500 text-xs">· Instagram</span>
              </div>
            )}
            {data.snapchat_handle && (
              <div className="flex items-center gap-2 text-sm" dir="ltr">
                <Camera className="w-4 h-4 text-rb-red shrink-0" />
                <span className="font-bold">@{data.snapchat_handle}</span>
                <span className="text-zinc-500 text-xs">· Snapchat</span>
              </div>
            )}
          </div>
        )}

        <div className="flex items-center gap-3 bg-rb-red/10 -mx-5 px-5 py-2.5">
          <Shield className="w-4 h-4 text-rb-red shrink-0" />
          <div className="flex-1">
            <div className="text-[9px] tracking-[0.25em] text-rb-red uppercase font-bold">عضوية رسمية</div>
            <div className="font-bold text-xs">نادي RB100 · المدينة المنورة · منذ ٢٠٠٢</div>
          </div>
        </div>

        {/* QR Code */}
        <div className="flex items-center gap-3 mt-auto">
          <div className="bg-white border border-zinc-300 p-1.5 shrink-0">
            <QRCodeSVG
              value={`${window.location.origin}/track?ref=${data.reference_code}`}
              size={72}
              level="M"
              bgColor="#FFFFFF"
              fgColor="#000000"
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[9px] tracking-[0.3em] text-zinc-500 uppercase mb-1">للتحقق</div>
            <div className="text-[11px] leading-snug text-zinc-700">
              امسح الباركود للتحقق من صلاحية العضوية في نادي RB100.
            </div>
          </div>
        </div>
      </div>

      {/* Sponsors strip */}
      <div className="bg-zinc-50 border-t border-zinc-200 px-3 py-2 shrink-0">
        <div className="text-[8px] tracking-[0.25em] text-zinc-500 uppercase text-center mb-1">
          برعاية
        </div>
        <div className="flex items-center justify-around gap-2">
          <div className="h-7 w-12 flex items-center justify-center bg-white">
            <img src={GOV_SPONSOR.logo} alt="" className="max-h-6 max-w-10 object-contain" />
          </div>
          {GOLD_SPONSORS.map((s, i) => (
            <div key={i} className="h-7 w-12 flex items-center justify-center bg-white">
              <img src={s.logo} alt="" className="max-h-6 max-w-10 object-contain" />
            </div>
          ))}
        </div>
      </div>

      {/* Footer with reference code */}
      <div className="bg-black text-white px-5 py-3 flex items-center justify-between shrink-0">
        <div>
          <div className="text-[9px] tracking-[0.3em] text-white/60 uppercase">رقم العضوية</div>
          <div className="font-mono font-bold text-base tracking-widest text-rb-red">
            {data.reference_code}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[9px] tracking-[0.3em] text-white/60 uppercase">عضو</div>
          <div className="text-xs font-bold">RB100 معتمد</div>
        </div>
      </div>

      <div className="h-1.5 bg-rb-red shrink-0" />
    </div>
  );
};
